import BackButton from 'components/backButton'
import { Button, Spinner } from 'components/common'
import {
  CreateRmaModal,
  OrderDetailsSection,
  TableRmaHistory,
} from 'components/orderManagement'
import { usePageAccess } from 'hooks'
import useModal from 'hooks/useModal'
import { useEffect, useMemo, useState } from 'react'
import { Helmet } from 'react-helmet'
import { useNavigate, useParams } from 'react-router-dom'
import { OrderType } from 'types/order'
import { fetchOrder } from 'utils/api/orderApi'
import {
  ORDER_TABLE_FIELDS,
  PAGE_ADMIN_ORDERS,
  PAGE_NOT_FOUND,
  PAGE_PARTIAL_RMA,
} from 'utils/constants'

const { ORDER_NO } = ORDER_TABLE_FIELDS

const OrderDetailsPage = () => {
  const navigate = useNavigate()
  const { orderId: orderNo } = useParams()

  const [order, setOrder] = useState<OrderType>()
  const [loading, setLoading] = useState<boolean>(true)
  const [refetch, setRefetch] = useState<boolean>(false)

  const { isEditor } = usePageAccess()

  const getOrderDetails = async (orderId: string) => {
    try {
      const { data } = await fetchOrder(orderId)
      setOrder({ ...data })
    } catch (error) {
      navigate(PAGE_NOT_FOUND.path)
    } finally {
      setLoading(false)
    }
  }

  const { isFullRmaGenerated, isPartialRmaGenerated } = useMemo(() => {
    if (order?.rma && order.rma?.length > 0 && order?.isFullRMA !== undefined) {
      const fullRma = order?.isFullRMA ?? order.rma[0].isFullRMA
      return {
        isFullRmaGenerated: fullRma,
        isPartialRmaGenerated: !fullRma,
      }
    }

    return { isFullRmaGenerated: false, isPartialRmaGenerated: false }
  }, [order?.rma, order?.isFullRMA])

  useEffect(() => {
    if (orderNo) {
      getOrderDetails(orderNo)
    }
  }, [refetch])

  const { closeModal, isOpen, openModal } = useModal()

  const redirectToPartialRma = () => {
    navigate(`${PAGE_PARTIAL_RMA.path}/${orderNo}/generate`)
  }

  const redirectToOrdersPage = () => {
    navigate(PAGE_ADMIN_ORDERS.path)
  }

  if (loading) {
    return <Spinner />
  }

  return (
    <>
      <Helmet>
        <title>ORMS Order Detail </title>
      </Helmet>

      {isEditor && (
        <CreateRmaModal
          onSuccess={() => {
            setRefetch(!refetch)
          }}
          orderId={order!._id}
          orderNo={orderNo!}
          closeModal={closeModal}
          isOpen={isOpen}
        />
      )}

      <div className="flex items-center justify-between">
        <BackButton handleBack={redirectToOrdersPage} />

        {isEditor && !isFullRmaGenerated && (
          <div className="flex items-center justify-end gap-2">
            {!isPartialRmaGenerated && (
              <Button
                buttonText="Create RMA"
                type="button"
                onClick={() => openModal()}
              />
            )}
            <Button
              buttonText="Create Partial RMA"
              type="button"
              onClick={redirectToPartialRma}
            />
          </div>
        )}
      </div>

      {order && ORDER_NO.value in order && (
        <>
          <OrderDetailsSection order={order} />
          <TableRmaHistory
            isFullRmaGenerated={isFullRmaGenerated}
            rmaList={order?.rma && order?.rma.length > 0 ? order.rma : []}
          />
        </>
      )}
    </>
  )
}

export default OrderDetailsPage
